/**
 * Types for the marketing demo module
 */

export type ModuleType = 'rent-roll' | 't12-financials' | 'lease-abstract';

export interface ModuleConfig {
  id: ModuleType;
  title: string;
  description: string;
  icon: string; // Lucide icon name
  acceptedFileTypes: string[];
  sampleImage?: string;
  multipleFiles?: boolean; // Allow multiple file uploads
  maxFiles?: number; // Max number of files when multipleFiles is true
}

export interface DemoSubmissionRequest {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  file: File;
  moduleType: ModuleType;
}

export interface DemoSubmissionResponse {
  success: true;
  message: string;
  submissionId: number;
}

export interface DemoErrorResponse {
  success: false;
  error: string;
  message: string;
}

export interface RateLimitResponse {
  success: false;
  error: 'rate_limited';
  message: string;
  retryAfter: string; // ISO date string
}

export type DemoResponse = DemoSubmissionResponse | DemoErrorResponse | RateLimitResponse;

export function isRateLimitResponse(response: DemoResponse): response is RateLimitResponse {
  return !response.success && response.error === 'rate_limited';
}

export function isSuccessResponse(response: DemoResponse): response is DemoSubmissionResponse {
  return response.success === true;
}

// Module configurations
export const DEMO_MODULES: ModuleConfig[] = [
  {
    id: 't12-financials',
    title: 'Multifamily T12 Financials',
    description: 'Extract and categorize trailing 12-month income and expense data for your underwriting model',
    icon: 'TrendingUp',
    acceptedFileTypes: ['.pdf', '.xlsx', '.xls'],
    sampleImage: '/samples/t12-financials-sample.png'
  },
  {
    id: 'rent-roll',
    title: 'Multifamily Rent Rolls',
    description: 'Extract tenant data, unit mix, and income details from any rent roll PDF or Excel file',
    icon: 'BarChart3',
    acceptedFileTypes: ['.pdf', '.xlsx', '.xls'],
    sampleImage: '/samples/rent-roll-sample.png'
  },
  {
    id: 'lease-abstract',
    title: 'Commercial Lease Abstracts',
    description: 'Extract key terms from lease packages including amendments, assignments, and guaranties',
    icon: 'FileText',
    acceptedFileTypes: ['.pdf'],
    multipleFiles: true,
    maxFiles: 10
  }
];

export function getModuleConfig(moduleType: ModuleType): ModuleConfig {
  const config = DEMO_MODULES.find(m => m.id === moduleType);
  if (!config) {
    throw new Error(`Unknown module type: ${moduleType}`);
  }
  return config;
}
